import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

export type DateInput = string | number | Date | Dayjs | null | undefined;

export const DATE_FORMATS = {
  DEFAULT_DATE: "DD MMM YYYY",
  DEFAULT_DATE_TIME: "DD MMM YYYY, hh:mm A",
  SHORT_DATE: "DD MMM",
  TIME_12H: "hh:mm A",
  INPUT_DATE: "YYYY-MM-DD",
  DISPLAY_INPUT: "DD/MM/YYYY",
  MONTH_YEAR: "MMM YYYY",
  MONTH_YEAR_SHORT: "MMM YY",
  YEAR_MONTH_KEY: "YYYY-MM",
  MONTH_NAME: "MMMM",
} as const;

type DateDisplayConfig = {
  dateFormat: string;
  dateTimeFormat: string;
  shortDateFormat: string;
  timezone: string | null;
};

const defaultDateDisplayConfig: DateDisplayConfig = {
  dateFormat: DATE_FORMATS.DEFAULT_DATE,
  dateTimeFormat: DATE_FORMATS.DEFAULT_DATE_TIME,
  shortDateFormat: DATE_FORMATS.SHORT_DATE,
  timezone: null,
};

let dateDisplayConfig: DateDisplayConfig = { ...defaultDateDisplayConfig };

function applyConfiguredTimezone(date: Dayjs): Dayjs {
  if (!dateDisplayConfig.timezone) {
    return date;
  }

  return date.tz(dateDisplayConfig.timezone);
}

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_TIMEZONE_DESIGNATOR = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Pins a zone-less datetime string to UTC instead of letting dayjs read it as browser-local.
 * Mirrors the guard in platformOwner/utils/dateFormatting.ts. The backend's JSON contract
 * guarantees a 'Z' or offset on every instant it emits, so this is normally a no-op — it exists
 * so this util is correct independent of server state, not only correct because nothing today
 * violates the contract.
 *
 * A bare civil date ("2026-08-21", no time component) is left untouched: it denotes a calendar
 * day, not an instant, so there is no zone for it to be ambiguous about, and appending 'Z' to a
 * date-only string is not valid ISO 8601.
 */
function normalizeIsoInput(date: DateInput): DateInput {
  if (typeof date !== "string" || ISO_DATE_ONLY.test(date)) {
    return date;
  }

  return HAS_TIMEZONE_DESIGNATOR.test(date) ? date : `${date}Z`;
}

function parseDate(date: DateInput): Dayjs | null {
  if (date == null) {
    return null;
  }

  const parsed = dayjs(normalizeIsoInput(date));
  if (!parsed.isValid()) {
    return null;
  }

  return applyConfiguredTimezone(parsed);
}

function formatWithFallback(date: DateInput, format: string, fallback: string): string {
  const parsed = parseDate(date);
  if (!parsed) {
    return fallback;
  }

  return parsed.format(format);
}

export function setDateDisplayConfig(config: Partial<DateDisplayConfig>): void {
  dateDisplayConfig = {
    ...dateDisplayConfig,
    ...config,
  };
}

export function resetDateDisplayConfig(): void {
  dateDisplayConfig = { ...defaultDateDisplayConfig };
}

export function getDateDisplayConfig(): DateDisplayConfig {
  return { ...dateDisplayConfig };
}

export function formatDate(
  date: DateInput,
  format: string = dateDisplayConfig.dateFormat,
): string {
  if (date == null) {
    return "-";
  }

  return formatWithFallback(date, format, "-");
}

export function formatDateTime(
  date: DateInput,
  format: string = dateDisplayConfig.dateTimeFormat,
): string {
  if (date == null) {
    return "-";
  }

  return formatWithFallback(date, format, "-");
}

export function formatShortDate(date: DateInput): string {
  return formatDate(date, dateDisplayConfig.shortDateFormat);
}

export function formatTime(
  date: DateInput,
  format: string = DATE_FORMATS.TIME_12H,
): string {
  if (date == null) {
    return "-";
  }

  return formatWithFallback(date, format, "-");
}

export function formatMonthYear(date: DateInput): string {
  if (date == null) {
    return "-";
  }

  return formatWithFallback(date, DATE_FORMATS.MONTH_YEAR, "-");
}

export function formatMonthYearShort(date: DateInput): string {
  if (date == null) {
    return "-";
  }

  return formatWithFallback(date, DATE_FORMATS.MONTH_YEAR_SHORT, "-");
}

export function formatRelativeTime(date: DateInput, withoutSuffix = false): string {
  const parsed = parseDate(date);
  if (!parsed) {
    return "-";
  }

  return parsed.fromNow(withoutSuffix);
}

export function toDayjs(date: DateInput): Dayjs | null {
  return parseDate(date);
}

export function parseByFormat(date: string, format: string): Dayjs | null {
  const parsed = dayjs(date, format, true);
  if (!parsed.isValid()) {
    return null;
  }

  return applyConfiguredTimezone(parsed);
}

export function nowIsoString(): string {
  return dayjs().toISOString();
}

/** Today's calendar date (`YYYY-MM-DD`) in the configured school timezone, not the browser's. */
export function todayInSchoolTz(): string {
  return applyConfiguredTimezone(dayjs()).format(DATE_FORMATS.INPUT_DATE);
}

/** Alias of {@link todayInSchoolTz} — kept for the many existing call sites that already use it. */
export function nowIsoDate(): string {
  return todayInSchoolTz();
}

/**
 * The UTC instant of local midnight for `date` (`YYYY-MM-DD`) in the configured school timezone.
 * Use this instead of hand-building a `${date}T00:00:00Z` boundary, which anchors to UTC midnight
 * rather than the school's local midnight — wrong by the zone's offset for every tenant not on
 * UTC. Pair with {@link startOfNextDayInSchoolTzUtc} for a half-open `[from, toExclusive)` range,
 * matching the backend's `ITenantClock.DayRangeUtc`.
 */
export function startOfDayInSchoolTzUtc(date: string): string {
  const zone = dateDisplayConfig.timezone;
  const start = zone ? dayjs.tz(date, zone) : dayjs(date);
  return start.startOf("day").utc().toISOString();
}

/** The UTC instant of local midnight the day *after* `date`, in the configured school timezone. */
export function startOfNextDayInSchoolTzUtc(date: string): string {
  const zone = dateDisplayConfig.timezone;
  const start = zone ? dayjs.tz(date, zone) : dayjs(date);
  return start.startOf("day").add(1, "day").utc().toISOString();
}

export function isoStringMinutesFromNow(minutesFromNow: number): string {
  return dayjs().add(minutesFromNow, "minute").toISOString();
}

export function getCurrentYear(): number {
  return dayjs().year();
}

export function toUnixMs(date: DateInput): number {
  const parsed = parseDate(date);
  return parsed ? parsed.valueOf() : Number.NaN;
}

export function toStartOfDayUnixMs(date: DateInput): number {
  const parsed = parseDate(date);
  return parsed ? parsed.startOf("day").valueOf() : Number.NaN;
}

export function isAfter(date: DateInput, compareTo: DateInput): boolean {
  const parsed = parseDate(date);
  const compared = parseDate(compareTo);

  if (!parsed || !compared) {
    return false;
  }

  return parsed.isAfter(compared);
}

export function isSameOrBefore(date: DateInput, compareTo: DateInput): boolean {
  const parsed = parseDate(date);
  const compared = parseDate(compareTo);

  if (!parsed || !compared) {
    return false;
  }

  return parsed.isSame(compared) || parsed.isBefore(compared);
}

export function ageInYears(fromDate: DateInput, toDate: DateInput = dayjs()): number {
  const parsedFromDate = parseDate(fromDate);
  const parsedToDate = parseDate(toDate);

  if (!parsedFromDate || !parsedToDate) {
    return 0;
  }

  return parsedToDate.diff(parsedFromDate, "year");
}

export function monthNameUppercase(date: DateInput): string {
  const parsed = parseDate(date);
  if (!parsed) {
    return "";
  }

  return parsed.format(DATE_FORMATS.MONTH_NAME).toUpperCase();
}

export function yearMonthKey(date: DateInput): string {
  return formatWithFallback(date, DATE_FORMATS.YEAR_MONTH_KEY, "");
}

export function fromYearMonthKey(key: string): Dayjs | null {
  const parsed = dayjs(key, DATE_FORMATS.YEAR_MONTH_KEY, true);
  return parsed.isValid() ? applyConfiguredTimezone(parsed.startOf("month")) : null;
}
